<template>
  <main class="container container-trip-search d-flex flex-column">
    <CheckBoxComponent @onClick="isRoundTrip" :state="roundTrip" :id="'roundTrip'" name="roundTrip"
                       class="checkbox-default mb-4">
      <template #checkbox-text>
        <label class="ml-5" for="roundTrip">{{ $t('round_trip') }}</label>
      </template>
    </CheckBoxComponent>

    <TransportationComponent :round-trip="roundTrip" @on-search-submit="onSearchSubmit"/>
    <span class="custom-route-error-color font-small" v-for="error in backEndErrorMessage" :key="error">
      {{ $t(error) }}
    </span>

    <div id="main-transportation-title" class="d-flex flex-column align-items-center justify-content-center">
      <div class="font-big mt-5 transportation-title">{{ $t('passenger_transportation') }}</div>
      <h3 class="font-weight-600">{{ pickUp.address_to_show }}, {{ pickUp.city }} - {{ dropOff.address_to_show }}, {{ dropOff.city }}</h3>
    </div>

    <DatesSliderComponent
        :how-many-to-load="6"
        :selected-date="tripDepartureDate.toISOString()"
        :round-trip="roundTrip"
        @selected-date="onSelectDate"
    />

    <div class="mt-5 d-flex flex-column align-items-start">
      <AdditionalInfoCheckboxModal
          :id="'additional-pickup-info'"
          :upperText="$t('pickup_from_the_specified_address')"
          :lowerText="$t('enter_a_pickup_address')"
          :labelText="$t('pick_up_from_the_specified_address')"
          :init_is_checked="specifiedData.pickup_is_checked"
          :init_address="specifiedData.pickup_specified_address"
          :init_note="specifiedData.pickup_note"
          @changed-address="onChangeAddress($event)"
          @changed-note="onChangeNote($event)">
      </AdditionalInfoCheckboxModal>
      <AdditionalInfoCheckboxModal
          class="mt-2"
          :id="'additional-dropoff-info'"
          :upperText="$t('dropoff_at_specified_address')"
          :lowerText="$t('enter_dropoff_address')"
          :labelText="$t('drop_off_at_specified_address')"
          :init_is_checked="specifiedData.dropoff_is_checked"
          :init_address="specifiedData.dropoff_specified_address"
          :init_note="specifiedData.dropoff_note"
          @changed-address="onChangeAddress($event)"
          @changed-note="onChangeNote($event)">
      </AdditionalInfoCheckboxModal>
    </div>

    <!--looping available transportations-->
    <div class="transportation-routes-list-container d-flex flex-column align-items-center">
      <template v-if="isLoading">
        <div class="is-loading"></div>
      </template>
      <template v-else>
        <template v-for="transportation in transportations" :key="transportation.id">
          <TransportationItemComponent
              :placeholder="roundTrip ? 'select' : 'order_now'"
              :is-round-trip="roundTrip"
              :transportation="transportation"
              @select-transportation="onSelectTransportation">
          </TransportationItemComponent>
        </template>
      </template>
      <div class="trip-error-message" v-for="error in transportationsMessage" :key="error">
        {{ $t(error) }}
      </div>
    </div>


  </main>
</template>

<script lang="ts">
import {defineComponent} from "vue";
import {mapState, useStore} from "vuex";
import axios from "axios";
import {routesConst} from "../../routes";
import apiService from "../../services/apiService";
import apiTransportationsService from "../../services/apiTransportationsService";

import type {IOrderFalseRoundTripState} from "@/store/states/IOrderFalseRoundTripState.interface";
import type {IOrderState} from "@/store/states/IOrderState.interface";

// @ts-ignore
import TransportationComponent from "../../components/Forms/Transportation/TransportationComponent.vue";
// @ts-ignore
import TransportationItemComponent from "../../components/transportationItem/TransportationItemComponent.vue";
// @ts-ignore
import CheckBoxComponent from "../../components/CheckBox/CheckBoxComponent.vue";
// @ts-ignore
import TransportationTicketComponent from "../../components/transportationTicket/TransportationTicketComponent.vue";
// @ts-ignore
import DatesSliderComponent from "../../components/datesSlider/DatesSliderComponent.vue";
// @ts-ignore
import AdditionalInfoCheckboxModal from "../../components/tripAdditionalInfoCheckboxModal/tripAdditionalInfoCheckboxModalComponent.vue";

import apiGoogleService from "../../services/apiGoogleService";
import {dateHelpers} from "../../helpers/dateHelpers";
import type { ITransportationsRequestQueryParams } from "@/interfaces/IRequestQueryParams.interface";
import type {IApiResponseTransportation} from "@/interfaces/apiResponseInterfaces/IApiResponseTransportation.interface";


export default defineComponent({
  name: 'TripsPage',
  components: {
    TransportationComponent,
    TransportationItemComponent,
    CheckBoxComponent,
    TransportationTicketComponent,
    DatesSliderComponent,
    AdditionalInfoCheckboxModal,
  },

  setup() {
    const store = useStore();

    return {
      store
    }
  },

  data() {
    return {
      backEndErrorMessage: <string[]>[],
      transportationsMessage: <string[]>[],
      isLoading: false,
    }
  },

  computed: {
    ...mapState<IOrderState>('order', {
      roundTrip: (state: IOrderState) => state.roundTrip,
    }),
    ...mapState<IOrderFalseRoundTripState>('orderFalseRoundTrip', {
      pickUp: (state: IOrderFalseRoundTripState) => state.pickUp,
      dropOff: (state: IOrderFalseRoundTripState) => state.dropOff,
      tripDepartureDate: (state: IOrderFalseRoundTripState) => state.tripDepartureDate,
      transportations: (state: IOrderFalseRoundTripState) => state.transportations,
    }),

    tripObject() {
      const state = this.store.state.orderFalseRoundTrip;

      let params: ITransportationsRequestQueryParams |null = null;

      params = {
        'pickup': state.pickUp.id,
        'drop-off': state.dropOff.id,
        'pickup-date': dateHelpers.dateToDisplay(state.tripDepartureDate),
        'passengers': state.tripPassengers
      }
      return params;
    },

    specifiedData() {
      const state = this.store.state.orderFalseRoundTrip;

      let params = {
        'pickup_specified_address': state.pickup_specified_address,
        'dropoff_specified_address': state.dropoff_specified_address,
        'pickup_note': state.pickup_note,
        'dropoff_note': state.dropoff_note,
        'pickup_is_checked': (state.pickup_specified_address.length > 0) || (state.pickup_note.length > 0),
        'dropoff_is_checked': (state.dropoff_specified_address.length > 0) || (state.dropoff_note.length > 0)
      }

      return params;
    },
  },

  methods: {
    isRoundTrip: function ($event: {target: HTMLInputElement}): void {
      this.store.dispatch('order/updateInitStates', [{prop: 'roundTrip', value: $event.target.checked}]);

      this.$router.push(routesConst.ROUND_TRIP_FIRST_STEP);
    },

    onSelectDate($event: {date: string}): void {
      this.store.dispatch('orderFalseRoundTrip/updateState', [{prop: 'tripDepartureDate', value: new Date($event.date)}]);
      this.onSearchSubmit();
    },

    onSelectTransportation($event: {transportationId: number}): void {
      this.store.dispatch('orderFalseRoundTrip/updateState', [{prop: 'transportationId', value: $event.transportationId}]);
      this.$router.push(routesConst.ORDER);
    },

    onSearchSubmit(): void {

      this.backEndErrorMessage = [];
      this.transportationsMessage = [];
      this.isLoading = true;

      apiTransportationsService.getTransportations(this.tripObject)
          .then(response => {
            let isError = false;
            if (!response.data!.length) {
              this.transportationsMessage.push('transportations_not_found');
              isError = true;
              this.isLoading = false;
            }

            if (response.errors!.length) {
              this.backEndErrorMessage = [...<[]>response.errors];
              isError = true;
            }

            if (isError) {
              return;
            }

            const rawTransportations: IApiResponseTransportation[]|null = response.data;

            // @ts-ignore
            const data = rawTransportations.map((item) => {
              if (item.hasOwnProperty('map_response') && item['map_response']) {
                let map_response: any = null;
                try {
                  map_response = JSON.parse(item['map_response']);

                  if (Array.isArray(map_response)) {
                    map_response = apiGoogleService.deserializeRoute(map_response[0]);
                    item = { ...item, map_response };
                  } else {
                    console.error('Got map response but it\'s not an array as it supposed to');
                  }
                } catch (Exception) {
                  console.error(Exception);
                }
              }

              return item;
            });

            this.store.dispatch('orderFalseRoundTrip/updateState', [{prop: 'transportations', value: data}]);

            this.isLoading = false;
          });
    },

    onChangeAddress(event: {id: string, value: string}): void {
        switch (event.id) {
            case 'additional-pickup-info':
                this.store.dispatch('orderFalseRoundTrip/updateState', [{prop: 'pickup_specified_address', value: event.value}]);
                
                break;
            case 'additional-dropoff-info':
                this.store.dispatch('orderFalseRoundTrip/updateState', [{prop: 'dropoff_specified_address', value: event.value}]);

                break;
        }
    },

    onChangeNote(event: {id: string, value: string}): void {
        switch (event.id) {
            case 'additional-pickup-info':
                this.store.dispatch('orderFalseRoundTrip/updateState', [{prop: 'pickup_note', value: event.value}]);

                break;
            case 'additional-dropoff-info':
                this.store.dispatch('orderFalseRoundTrip/updateState', [{prop: 'dropoff_note', value: event.value}]);

                break;
        }
    }
  },

  created() {
    this.onSearchSubmit();

    axios.get(apiService.pageTripsPageEndpoint())
        .then((response) => {
          // ...
        });
  },

  mounted() {
    //
  }
});
</script>

<style lang="scss" scoped>
@import './trips.scss';
</style>